package fr.frux.feature.map

import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlin.time.Clock
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

private const val TAG = "NeverSeenInvite"

/**
 * Rayon de declenchement. Au-dessus de la derive d'un GPS de telephone sous couvert vegetal (5 a
 * 15 m) pour que « tu y es » reste vrai, et assez court pour qu'on voie l'arbre dont on parle.
 */
internal const val NEVER_SEEN_INVITE_RADIUS_METERS = 30.0

/**
 * Deplacement minimal depuis la derniere evaluation pour en refaire une. La position est suivie en
 * continu au premier plan : sans ce pas, chaque emission du GPS couterait un appel reseau. La
 * moitie du rayon, et non le rayon entier : ainsi on ne peut jamais arriver devant un arbre sans
 * qu'une evaluation ait eu lieu a moins de [NEVER_SEEN_INVITE_RADIUS_METERS] de lui.
 */
internal const val NEVER_SEEN_INVITE_REEVALUATION_METERS = NEVER_SEEN_INVITE_RADIUS_METERS / 2

/** Au-dela de cette distance de l'arbre, l'invitation n'a plus de sens et se retire d'elle-meme. */
internal const val NEVER_SEEN_INVITE_WITHDRAW_METERS = NEVER_SEEN_INVITE_RADIUS_METERS * 2

/** Intervalle minimal entre deux requetes, quel que soit le deplacement : borne le cout reseau. */
internal val NEVER_SEEN_INVITE_MIN_INTERVAL = 30.seconds

/**
 * Fenetre de silence entre deux sollicitations, tous arbres confondus. « Une par sortie » n'a pas
 * de definition technique - une session d'app n'en est pas une, puisque rouvrir l'app trois fois
 * ne fait pas trois sorties. Une duree glissante l'approche au plus juste, et elle vit ici, donc
 * elle se change sans migration ni rupture de contrat.
 */
internal val NEVER_SEEN_INVITE_COOLDOWN = 6.hours

/**
 * L'invitation en attente d'affichage : l'arbre le plus proche devant lequel personne n'est jamais
 * passe (ADR 0025), et son nom d'espece pour que le libelle nomme ce qu'on regarde.
 */
data class NeverSeenInvite(
    val treeId: Long,
    val speciesName: String,
    /** Position de l'arbre, pour retirer l'invitation quand la personne s'en eloigne (#734). */
    val treeLocation: Location? = null,
)

/**
 * Faut-il montrer l'invitation maintenant. Fonction pure, sur le patron de [selectMapNotice] : la
 * regle de cohabitation des controles flottants est testable sans harnais d'UI.
 *
 * Elle cede la place a tout le reste. Un bandeau d'info signale un probleme de ce que l'utilisateur
 * regarde, la pastille de re-requete repond a un geste qu'il vient de faire : les deux passent
 * avant une proposition que personne n'a demandee. Et l'empilement de controles sous la barre de
 * recherche est exactement ce que #163 a supprime, il ne revient pas par cette porte.
 */
internal fun showsNeverSeenInvite(
    invite: NeverSeenInvite?,
    notice: MapNotice?,
    searchAreaVisible: Boolean,
): Boolean = invite != null && notice == null && !searchAreaVisible

/**
 * Invitation a confirmer un arbre que personne n'a jamais vu, quand on se trouve devant (#396).
 *
 * Declenchement local a l'appareil : aucune position ne part au serveur pour ce besoin, et l'app
 * ne demande toujours que le premier plan. Le suivi de position ne tourne que carte visible et
 * app au premier plan, sans permission de plus ni suivi en arriere-plan : une invitation n'est donc
 * jamais evaluee (et son delai grille) pendant que la personne regarde un autre onglet.
 * L'evaluation reagit a chaque mise a jour de [MapState.userLocation] (cf. [MapViewModel]), mais
 * seulement apres un deplacement d'au moins [NEVER_SEEN_INVITE_REEVALUATION_METERS] et pas plus
 * d'une fois par [NEVER_SEEN_INVITE_MIN_INTERVAL]. L'invitation se retire d'elle-meme quand on
 * s'eloigne de l'arbre de plus de [NEVER_SEEN_INVITE_WITHDRAW_METERS], sans compter comme un refus.
 *
 * Le geste demande n'est jamais un « oui » isole : l'invitation ouvre la fiche, ou vivent
 * l'observation de terrain et la photo, les deux seuls gestes qui portent de l'information
 * (ADR 0025).
 */
internal class NeverSeenInviteController(
    private val treeRepository: TreeRepository,
    private val store: NeverSeenInviteStore,
    private val state: MutableStateFlow<MapState>,
    private val now: () -> Instant = { Clock.System.now() },
) {
    /**
     * Refus dans cette session. S'ajoute au plafond persistant sans le remplacer : fermer
     * l'invitation doit valoir pour la sortie en cours, meme si la fenetre de silence expire
     * entre-temps.
     */
    private var dismissedThisSession = false

    /**
     * Position de la derniere requete aboutie, pour ne pas reinterroger sur un deplacement
     * infime. Posee seulement au succes : apres un echec reseau, la position suivante retente.
     */
    private var lastEvaluatedAt: Location? = null

    /** Instant de la derniere tentative, reussie ou non : le plancher de temps vaut aussi pour les echecs. */
    private var lastAttemptAt: Instant? = null

    /** Une position fraiche vient d'etre obtenue. Silencieux sur tous les chemins de refus. */
    suspend fun onUserLocation(location: Location) {
        withdrawIfFarFrom(location)
        if (!canInvite() || !isDueForEvaluation(location)) return
        lastAttemptAt = now()

        // Pas de lat/lon dans les logs : position utilisateur = donnee personnelle (#175).
        val result =
            treeRepository.nearbyNeverSeen(location.latitude, location.longitude, NEVER_SEEN_INVITE_RADIUS_METERS)
        result.onFailure { Logger.d(TAG, "Recherche d'arbre jamais vu echouee, invitation ignoree") }
        if (result.isSuccess) lastEvaluatedAt = location
        result
            .getOrNull()
            ?.let { tree ->
                // Le plafond se pose a l'affichage et non au tap : une invitation ignoree a quand
                // meme consomme l'attention de la personne.
                store.markShown(now())
                state.update {
                    it.copy(
                        neverSeenInvite =
                            NeverSeenInvite(
                                treeId = tree.id,
                                speciesName = tree.species.commonNameFr,
                                treeLocation = Location(tree.latitude, tree.longitude),
                            ),
                    )
                }
            }
    }

    /** Pas de requete sous le plancher de temps, ni sans deplacement depuis la derniere aboutie. */
    private fun isDueForEvaluation(location: Location): Boolean {
        val attemptedAt = lastAttemptAt
        if (attemptedAt != null && now() - attemptedAt < NEVER_SEEN_INVITE_MIN_INTERVAL) return false
        val previous = lastEvaluatedAt
        return previous == null || previous.distanceMetersTo(location) >= NEVER_SEEN_INVITE_REEVALUATION_METERS
    }

    /**
     * La personne a continue sa route : l'invitation ne parle plus de ce qu'elle a sous les yeux.
     * Ce n'est pas un refus, la session n'est pas marquee et le plafond persistant reste tel quel.
     */
    private fun withdrawIfFarFrom(location: Location) {
        val treeLocation = state.value.neverSeenInvite?.treeLocation ?: return
        if (treeLocation.distanceMetersTo(location) > NEVER_SEEN_INVITE_WITHDRAW_METERS) clear()
    }

    /** Le plafond se verifie avant la requete : une sollicitation refusee ne coute pas un appel. */
    private suspend fun canInvite(): Boolean {
        if (dismissedThisSession || state.value.neverSeenInvite != null) return false

        val lastShownAt = store.lastShownAt()
        return lastShownAt == null || now() - lastShownAt >= NEVER_SEEN_INVITE_COOLDOWN
    }

    /** L'invitation est fermee : plus rien pour cette session. */
    fun onDismissed() {
        dismissedThisSession = true
        clear()
    }

    /** L'invitation est suivie : la fiche prend le relais, le controle flottant disparait. */
    fun onAccepted() {
        clear()
    }

    /** Purge de session (#48), voir [MapViewModel.reset]. */
    fun reset() {
        dismissedThisSession = false
        lastEvaluatedAt = null
        lastAttemptAt = null
        clear()
    }

    private fun clear() {
        state.update { it.copy(neverSeenInvite = null) }
    }
}
